<template>
    <v-avatar :size="size" :class="avatarClass" @click="$emit('click')">
        <v-img 
            v-if="avatarUrl && !imageError" 
            :src="$showImage(avatarUrl)"
            @error="onImageError"
        />
        <div 
            v-else 
            class="user-avatar-initials"
            :style="{ fontSize: fontSize }"
        >
            {{ initials }}
        </div>
    </v-avatar>
</template>

<script>
export default {
    name: 'UserAvatar',
    data() {
        return {
            imageError: false
        }
    },
    props: {
        // URL de l'avatar (peut être l'objet complet ou juste l'URL)
        avatar: {
            type: [Object, String],
            default: null
        },
        // Prénom de l'utilisateur
        firstName: {
            type: String,
            default: ''
        },
        // Nom de l'utilisateur
        lastName: {
            type: String,
            default: ''
        },
        // Taille de l'avatar
        size: {
            type: [Number, String],
            default: 36
        },
        // Classes additionnelles
        avatarClass: {
            type: String,
            default: ''
        },
        // Si c'est une entreprise (pour afficher les 2 premières lettres du nom)
        isCompany: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        avatarUrl() {
            if (!this.avatar) return null;
            
            // Si c'est une string, on la retourne directement
            if (typeof this.avatar === 'string') {
                return this.avatar;
            }
            
            // Gestion des différents formats d'objets Strapi
            // Format: avatar.url
            if (this.avatar.url) {
                return this.avatar.url;
            }
            
            // Format: avatar.attributes.url (Strapi v4 single media)
            if (this.avatar.attributes?.url) {
                return this.avatar.attributes.url;
            }
            
            // Format: avatar.data.attributes.url
            if (this.avatar.data?.attributes?.url) {
                return this.avatar.data.attributes.url;
            }
            
            // Format: avatar.formats.thumbnail.url ou avatar.formats.medium.url
            if (this.avatar.formats) {
                return this.avatar.formats.medium?.url || 
                       this.avatar.formats.thumbnail?.url || 
                       this.avatar.formats.small?.url;
            }
            
            // Format: avatar.attributes.formats (Strapi v4 single media)
            if (this.avatar.attributes?.formats) {
                const formats = this.avatar.attributes.formats;
                return formats.medium?.url || 
                       formats.thumbnail?.url || 
                       formats.small?.url;
            }
            
            // Format: avatar.data.attributes.formats
            if (this.avatar.data?.attributes?.formats) {
                const formats = this.avatar.data.attributes.formats;
                return formats.medium?.url || 
                       formats.thumbnail?.url || 
                       formats.small?.url;
            }
            
            return null;
        },
        initials() {
            // Pour une entreprise : première lettre majuscule + deuxième lettre minuscule
            if (this.isCompany) {
                const name = this.firstName || this.lastName || '';
                if (name.length >= 2) {
                    return name.charAt(0).toUpperCase() + name.charAt(1).toLowerCase();
                }
                return name.charAt(0)?.toUpperCase() || '?';
            }
            
            // Pour un utilisateur : initiales prénom + nom
            const first = this.firstName?.charAt(0)?.toUpperCase() || '';
            const last = this.lastName?.charAt(0)?.toUpperCase() || '';
            
            // Retourne les deux initiales si disponibles, sinon une seule
            if (first && last) {
                return first + last;
            }
            return first || last || '?';
        },
        fontSize() {
            const numSize = parseInt(this.size);
            // Ajuste la taille de la police en fonction de la taille de l'avatar
            if (numSize >= 100) return '2.5em';
            if (numSize >= 60) return '1.8em';
            if (numSize >= 40) return '1.2em';
            return '0.9em';
        }
    },
    methods: {
        onImageError() {
            this.imageError = true;
        }
    },
    watch: {
        avatar() {
            // Réinitialise l'erreur quand l'avatar change
            this.imageError = false;
        }
    }
}
</script>

<style scoped>
.user-avatar-initials {
    background: black;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: #FFE200;
    font-weight: bold;
    font-family: 'NeueMachina-Light', sans-serif;
    padding-top: 0.2em;
    line-height: 1;
}
</style>
